import { useGSAP } from '@gsap/react';
import { DeviceMobileIcon, HandbagIcon, SparkleIcon } from '@phosphor-icons/react';
import { gsap } from 'gsap';
import { useRef } from 'react';

import { useContent } from '@/content/ContentContext';
import { Chapter, ChapterColumn } from '@/features/journey/Chapter';
import { PerfumeApp } from '@/features/mobile/apps/PerfumeApp';
import { WatchApp } from '@/features/mobile/apps/WatchApp';
import { MOTION_ALLOWED } from '@/motion/queries';
import { cx } from '@/ui/classNames';
import { PhoneFrame } from '@/ui/Device/PhoneFrame';
import { Container } from '@/ui/Layout/Container';
import { RevealText } from '@/ui/Reveal/RevealText';
import { typography } from '@/ui/Typography/typography';

import styles from './MobileShowcase.module.css';

const FEATURE_ICONS = [DeviceMobileIcon, HandbagIcon, SparkleIcon] as const;

/**
 * Chapitre 04, les applications, près du trou noir « Orbite ». Les deux
 * téléphones sont ses satellites : ils flottent devant lui, pivotent et
 * dérivent au défilement, pendant que le texte se tient en face.
 */
export function MobileShowcase() {
  const { mobile } = useContent();
  const sectionRef = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const media = gsap.matchMedia();
      media.add(MOTION_ALLOWED, () => {
        const trigger = {
          trigger: sectionRef.current,
          start: 'top bottom',
          end: 'bottom top',
          scrub: true,
        };
        // y/z explicites : GSAP reprend la main sur toute la transformation CSS.
        gsap.fromTo(
          '[data-phone="front"]',
          { x: '-6vw', y: 0, z: 0, rotateY: -28, rotateX: 10, yPercent: -40 },
          {
            x: '3vw',
            y: 0,
            z: 0,
            rotateY: -6,
            rotateX: 2,
            yPercent: -62,
            ease: 'none',
            scrollTrigger: trigger,
          },
        );
        gsap.fromTo(
          '[data-phone="back"]',
          { x: '-2vw', y: 0, z: -120, rotateY: -20, rotateX: 8, yPercent: -30 },
          {
            x: '5vw',
            y: 0,
            z: -120,
            rotateY: -10,
            rotateX: 0,
            yPercent: -56,
            ease: 'none',
            scrollTrigger: trigger,
          },
        );
      });
      return () => {
        media.revert();
      };
    },
    { scope: sectionRef },
  );

  const { watch, perfume } = mobile.apps;

  return (
    <Chapter
      ref={sectionRef}
      chapter="applications"
      id="applications"
      className={styles.section}
      aria-labelledby="mobile-title"
    >
      <Container className={styles.layout}>
        <figure className={styles.satellites}>
          <div className={styles.stage}>
            <div className={styles.halo} aria-hidden="true" data-cosmos-decor />
            <div className={styles.phones}>
              <div className={styles.phoneBack} data-phone="back">
                <PhoneFrame className={styles.floatSlow} label={perfume.label}>
                  <PerfumeApp content={perfume} />
                </PhoneFrame>
              </div>
              <div className={styles.phoneFront} data-phone="front">
                <PhoneFrame className={styles.float} label={watch.label}>
                  <WatchApp content={watch} />
                </PhoneFrame>
              </div>
            </div>
          </div>
          <figcaption className={styles.caption}>{mobile.caption}</figcaption>
        </figure>

        <ChapterColumn chapter="applications" className={styles.copy}>
          <RevealText id="mobile-title" className={cx(typography.heading, typography.h2)}>
            {mobile.title} <span className={typography.accent}>{mobile.titleAccent}</span>
          </RevealText>
          <p className={typography.lead}>{mobile.lead}</p>
          <ul className={styles.features}>
            {mobile.features.map((feature, index) => {
              const Icon = FEATURE_ICONS[index % FEATURE_ICONS.length] ?? SparkleIcon;
              return (
                <li key={feature.title} className={styles.feature}>
                  <span className={styles.featureIcon} aria-hidden="true">
                    <Icon size={20} weight="light" />
                  </span>
                  <span className={styles.featureTitle}>{feature.title}</span>
                  <span className={styles.featureDescription}>{feature.description}</span>
                </li>
              );
            })}
          </ul>
        </ChapterColumn>
      </Container>
    </Chapter>
  );
}
